import { SupportPhoneContext } from './lib/brand';
import { CarTaxiFront, House, Radio as RadioIcon, Route as RouteIcon, User } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { BrowserRouter, NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { SessionGate, type SessionMe, Spinner } from '@despachador/ui';
import { api } from './lib/api';
import { registerServiceWorker } from './lib/device';
import { unlockAudio } from './lib/alerts';
import { DriverProvider, useDriver } from './store';
import { CallOverlay, OfferExpired, OfferOverlay } from './components';
import { ConnectionStatus } from './panic';
import { startPanic } from './lib/panic';
import { Home } from './pages/Home';
import { Trips } from './pages/Trips';
import { Account, Documents, Quota, Vehicle } from './pages/Account';
import { Alerts, Notifications, Security } from './pages/Settings';
import { RadioPage, RadioStrip } from './pages/Radio';

/** A driver account: the driver app permission and a driver record (the API checks the same). */
const isDriver = (me: SessionMe) => me.permissions.includes('driver.app') && !!me.user.driverId;

export function App() {
  useEffect(() => void registerServiceWorker(), []);
  return (
    <BrowserRouter>
      <Gate />
    </BrowserRouter>
  );
}

/** One start-up for the app: the session, the screens without one and the account (SessionGate). */
function Gate() {
  const location = useLocation();
  const navigate = useNavigate();
  const [branding, setBranding] = useState<{ brandName: string; logoUrl?: string | null; supportPhone?: string | null } | null>(null);
  return (
    <SessionGate
      api={api}
      app="driver"
      appName="Conductor"
      subtitle="Ingreso de conductores"
      platform="PWA"
      large
      layout="split"
      router={{ pathname: location.pathname, search: location.search, navigate: (to, opts) => navigate(to, opts) }}
      acceptAccount={(me) => (isDriver(me) ? null : 'app-no-permitida')}
      onBranding={(b) => {
        setBranding(b);
        document.getElementById('manifest')?.setAttribute('href', `/public/manifest.webmanifest?app=driver&tenant=${encodeURIComponent(b.tenantSlug)}`);
      }}
      // the sign-in tap is a user gesture: unlock alert audio early
      onSignInGesture={() => void unlockAudio()}
    >
      {({ me, signOut }) => (
        <DriverProvider>
          <SupportPhoneContext.Provider value={branding?.supportPhone ?? null}>
            <Shell branding={branding ?? me.branding} onLogout={() => void signOut()} />
          </SupportPhoneContext.Provider>
        </DriverProvider>
      )}
    </SessionGate>
  );
}

const TITLES: Record<string, string> = {
  '/': 'Inicio',
  '/viajes': 'Viajes',
  '/radio': 'Radio',
  '/cuenta': 'Cuenta',
  '/cuenta/cuota': 'Cuota',
  '/cuenta/vehiculo': 'Vehículo',
  '/cuenta/documentos': 'Documentos',
  '/cuenta/alertas': 'Sonido y alertas',
  '/cuenta/notificaciones': 'Notificaciones',
  '/cuenta/seguridad': 'Seguridad',
};

function Shell({ branding, onLogout }: { branding: { brandName: string; logoUrl?: string | null }; onLogout: () => void }) {
  const { loading, loadError, refresh, socketState, me, active, radio, offer } = useDriver();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const onRadioPage = pathname === '/radio';
  const [retrying, setRetrying] = useState(false);
  // a trip that starts (offer accepted, or assigned by the central) takes the driver to it:
  // nobody should have to look for the service they just accepted
  const activeId = active?.id ?? null;
  const lastActive = useRef<string | null>(null);
  useEffect(() => {
    if (activeId && activeId !== lastActive.current && pathname !== '/') navigate('/');
    lastActive.current = activeId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);
  // the silent alert (PNC-001) belongs to the signed-in driver: queued alerts go out once the profile is known
  const userId: string | undefined = me?.profile?.user_id;
  useEffect(() => (userId ? startPanic(userId) : undefined), [userId]);
  const brandName = branding?.brandName ?? 'Conductor';
  const onNav = (path: string) => (document.title = `${TITLES[path] ?? 'Conductor'} · ${brandName}`);
  // links inside the pages (account → history) also update the tab title
  useEffect(() => void onNav(pathname), [pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="app">
      <header className="topbar">
        {branding?.logoUrl ? <img src={branding.logoUrl} alt="" height={24} /> : null}
        <span className="brand">{brandName}</span>
        <span className="spacer" />
        {/* link to the central, not availability; also the silent alert's trigger (hold 3 s) */}
        <ConnectionStatus socketState={socketState} disabled={!!offer} />
      </header>
      {socketState === 'closed' && (
        <div className="offline-banner" role="alert">
          Sin conexión con la central: no recibirás servicios hasta que vuelva. Reintentando…
        </div>
      )}
      <main className="content">
        {loading ? (
          <Spinner block label="Cargando tu información" />
        ) : !me ? (
          <div className="card stack" role="alert">
            <b>No pudimos cargar tu información</b>
            <div className="muted">{loadError ?? 'Inténtalo de nuevo.'}</div>
            <button
              className="btn primary block"
              disabled={retrying}
              onClick={async () => {
                setRetrying(true);
                await refresh();
                setRetrying(false);
              }}
            >
              {retrying ? 'Reintentando…' : 'Reintentar'}
            </button>
            <button className="btn block" onClick={onLogout}>
              Cerrar sesión
            </button>
          </div>
        ) : (
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/viajes" element={<Trips />} />
            <Route path="/radio" element={<RadioPage />} />
            <Route path="/cuenta" element={<Account onLogout={onLogout} />} />
            <Route path="/cuenta/cuota" element={<Quota />} />
            <Route path="/cuenta/vehiculo" element={<Vehicle />} />
            <Route path="/cuenta/documentos" element={<Documents />} />
            <Route path="/cuenta/alertas" element={<Alerts />} />
            <Route path="/cuenta/notificaciones" element={<Notifications />} />
            <Route path="/cuenta/seguridad" element={<Security onLogout={onLogout} />} />
            {/* addresses from before DRV-001 (bookmarks, notifications already sent) */}
            <Route path="/servicio" element={<Navigate to="/" replace />} />
            <Route path="/historial" element={<Navigate to="/viajes" replace />} />
            <Route path="/ajustes" element={<Navigate to="/cuenta/alertas" replace />} />
            <Route path="/seguridad" element={<Navigate to="/cuenta/seguridad" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )}
      </main>
      <nav className="bottom" aria-label="Secciones">
        <NavLink to="/" end onClick={() => onNav('/')}>
          {active ? <CarTaxiFront className="ico" size={22} aria-hidden /> : <House className="ico" size={22} aria-hidden />}
          Inicio
          {active && (
            <span className="nav-dot">
              <span className="sr-only">(servicio en curso)</span>
            </span>
          )}
        </NavLink>
        <NavLink to="/viajes" onClick={() => onNav('/viajes')}>
          <RouteIcon className="ico" size={22} aria-hidden />
          Viajes
        </NavLink>
        {radio && (
          <NavLink to="/radio" onClick={() => onNav('/radio')}>
            <RadioIcon className="ico" size={22} aria-hidden />
            Radio
          </NavLink>
        )}
        <NavLink to="/cuenta" onClick={() => onNav('/cuenta')}>
          <User className="ico" size={22} aria-hidden />
          Cuenta
        </NavLink>
      </nav>
      {/* the radio strip: every screen without a trip; the trip carries its own talk button, the radio page is the radio */}
      {me && !onRadioPage && !active && <RadioStrip />}
      <OfferOverlay />
      <OfferExpired />
      <CallOverlay />
    </div>
  );
}
